import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { UserCog, ShieldCheck } from 'lucide-react';

/**
 * Modal dialog to update assigned users for a case.
 * Loads /users, shows checkbox list, validates non-empty, calls
 * PATCH /cases/{id}/assignees on save.
 */
const AssignCaseDialog = ({ open, onOpenChange, caseItem, onSaved }) => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/users')
      .then((r) => setUsers(r.data || []))
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
    setSelected(caseItem?.assigned_users || []);
  }, [open, caseItem]);

  const toggle = (uid) => {
    setSelected((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      toast.error('At least one assigned user is required');
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/cases/${caseItem.id}/assignees`, {
        assigned_users: selected,
      });
      toast.success('Assignment updated');
      onOpenChange(false);
      if (onSaved) onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="assign-case-dialog">
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            <UserCog size={20} className="text-[#1e3a5f]" />
            Assign Case
          </DialogTitle>
          <DialogDescription>
            Select users who should have edit access to <span className="font-medium">{caseItem?.case_name}</span>.
            At least one user is required.
          </DialogDescription>
        </DialogHeader>

        <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
          {loading ? (
            <p className="p-4 text-sm text-slate-400 text-center">Loading users...</p>
          ) : users.length === 0 ? (
            <p className="p-4 text-sm text-slate-400 text-center">No users found.</p>
          ) : (
            users.map((u) => {
              const isCreator = u.id === caseItem?.created_by;
              return (
                <label
                  key={u.id}
                  htmlFor={`assign-${u.id}`}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50"
                  data-testid={`assign-row-${u.id}`}
                >
                  <Checkbox
                    id={`assign-${u.id}`}
                    checked={selected.includes(u.id)}
                    onCheckedChange={() => toggle(u.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                      {u.name || u.email}
                      {u.role === 'super_admin' && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-[#1e3a5f] bg-[#1e3a5f]/10 px-1.5 py-0.5 rounded">
                          <ShieldCheck size={10} /> Admin
                        </span>
                      )}
                      {isCreator && (
                        <span className="text-[10px] font-semibold uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                          Creator
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  </div>
                </label>
              );
            })
          )}
        </div>

        <p className="text-xs text-slate-500">
          {selected.length} user{selected.length === 1 ? '' : 's'} selected.
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="assign-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || selected.length === 0}
            className="bg-[#1e3a5f] hover:bg-[#162c46]"
            data-testid="assign-save-btn"
          >
            {saving ? 'Saving...' : 'Save Assignment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignCaseDialog;
